import {
  autocompletion,
  completionStatus,
  startCompletion,
  type CompletionContext
} from '@codemirror/autocomplete';
import { history, historyKeymap, defaultKeymap } from '@codemirror/commands';
import { EditorState, RangeSetBuilder, type Extension } from '@codemirror/state';
import {
  Decoration,
  EditorView,
  ViewPlugin,
  WidgetType,
  keymap,
  placeholder as placeholderText,
  type DecorationSet,
  type ViewUpdate
} from '@codemirror/view';
import { useEffect, useRef, type FC } from 'react';
import { cn } from '../lib/utils';
import { ziggizEditorTheme } from './code-mirror-theme';

interface TokenInputProps {
  values: readonly string[];
  onChange: (values: string[]) => void;
  /** Accessible name for the field. */
  label: string;
  placeholder?: string;
  /** Offered as you type, minus the values already chosen. */
  suggestions?: readonly string[];
  disabled?: boolean;
  className?: string;
}

const SEPARATOR = ', ';

/** `1, 4688, ` → `['1', '4688']`. */
const parseTokens = (text: string) =>
  text
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);

interface Segment {
  from: number;
  /** End of the value plus its separator, so a chip swallows its comma. */
  to: number;
  text: string;
}

const segments = (doc: string): Segment[] => {
  const found: Segment[] = [];
  const pattern = /[^,]+/g;
  for (const match of doc.matchAll(pattern)) {
    const text = match[0].trim();
    if (!text) continue;
    const start = (match.index ?? 0) + match[0].indexOf(text);
    let end = (match.index ?? 0) + match[0].length;
    // Absorb the comma and any spaces after it.
    if (doc[end] === ',') end += 1;
    while (doc[end] === ' ') end += 1;
    found.push({ from: start, to: end, text });
  }
  return found;
};

class ChipWidget extends WidgetType {
  readonly text: string;
  constructor(text: string) {
    super();
    this.text = text;
  }
  override eq(other: ChipWidget) {
    return other.text === this.text;
  }
  override toDOM(view: EditorView) {
    const chip = document.createElement('span');
    chip.className = 'cm-token';
    chip.textContent = this.text;
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'cm-token-remove';
    remove.setAttribute('aria-label', `Remove ${this.text}`);
    remove.textContent = '×';
    remove.addEventListener('mousedown', (event) => {
      event.preventDefault();
      const at = view.posAtDOM(chip);
      const segment = segments(view.state.doc.toString()).find(
        (item) => item.from === at
      );
      if (segment) view.dispatch({ changes: { from: segment.from, to: segment.to } });
    });
    chip.append(remove);
    return chip;
  }
  override ignoreEvent() {
    return false;
  }
}

/** Every value becomes a chip except the one the caret is in, which stays text to edit. */
const buildChips = (view: EditorView): DecorationSet => {
  const builder = new RangeSetBuilder<Decoration>();
  const doc = view.state.doc.toString();
  const caret = view.state.selection.main.head;
  const focused = view.hasFocus;
  const all = segments(doc);
  all.forEach((segment, index) => {
    const editing =
      focused && caret >= segment.from && caret <= segment.from + segment.text.length;
    // The last value, while focused and without a trailing comma, is still being typed.
    const drafting =
      focused && index === all.length - 1 && !doc.slice(segment.from).includes(',');
    if (editing || drafting) return;
    builder.add(
      segment.from,
      segment.to,
      Decoration.replace({ widget: new ChipWidget(segment.text) })
    );
  });
  return builder.finish();
};

const chips = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = buildChips(view);
    }
    update(update: ViewUpdate) {
      if (update.docChanged || update.selectionSet || update.focusChanged)
        this.decorations = buildChips(update.view);
    }
  },
  {
    decorations: (plugin) => plugin.decorations,
    // Chips are one unit: the caret steps over them and ⌫ removes a whole one.
    provide: (plugin) =>
      EditorView.atomicRanges.of(
        (view) => view.plugin(plugin)?.decorations ?? Decoration.none
      )
  }
);

const tokenTheme = EditorView.theme({
  // 7 + 24 + 7 + the 1px border = 40, the height of the selects beside it.
  '.cm-content': {
    padding: '7px 0',
    fontFamily: 'var(--font-mono, ui-monospace, monospace)',
    fontSize: '12px'
  },
  '.cm-line': { padding: '0 8px', lineHeight: '24px' },
  '.cm-activeLine': { backgroundColor: 'transparent' },
  '.cm-token': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    height: '22px',
    margin: '0 4px 0 0',
    padding: '0 2px 0 6px',
    borderRadius: '5px',
    border: '1px solid var(--z-brand-border)',
    backgroundColor: 'var(--z-brand-subtle)',
    color: 'var(--z-brand)',
    verticalAlign: 'middle',
    lineHeight: '20px'
  },
  '.cm-token-remove': {
    width: '16px',
    height: '16px',
    border: 'none',
    borderRadius: '3px',
    background: 'transparent',
    color: 'inherit',
    cursor: 'pointer',
    lineHeight: '14px'
  },
  '.cm-token-remove:hover': { backgroundColor: 'var(--z-brand-subtle)' }
});

const suggestionSource = (options: readonly string[]) => (context: CompletionContext) => {
  const word = context.matchBefore(/[^,\s][^,]*/);
  if (!word && !context.explicit) return null;
  const taken = new Set(parseTokens(context.state.doc.toString()));
  return {
    from: word?.from ?? context.pos,
    options: options
      .filter((option) => !taken.has(option))
      .map((option) => ({ label: option, apply: `${option}${SEPARATOR}` })),
    validFor: /^[^,]*$/
  };
};

/**
 * Several values in one field, on CodeMirror. The value is plain text
 * (`1, 4688`); each value draws as a chip. Type a comma or ↵ to add one,
 * ⌫ removes a whole chip, ↓ opens the suggestions. Pasting `1, 4688` adds both.
 */
export const TokenInput: FC<TokenInputProps> = ({
  values,
  onChange,
  label,
  placeholder,
  suggestions,
  disabled = false,
  className
}) => {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<EditorView | null>(null);
  const handlers = useRef({ onChange, values });
  useEffect(() => {
    handlers.current = { onChange, values };
  });
  const suggestionKey = JSON.stringify(suggestions ?? []);

  useEffect(() => {
    if (!host.current) return;
    const options = JSON.parse(suggestionKey) as string[];
    const extensions: Extension[] = [
      history(),
      // One line only: a pasted newline becomes a separator.
      EditorState.transactionFilter.of((tr) =>
        tr.newDoc.lines > 1
          ? [
              tr,
              {
                changes: {
                  from: 0,
                  to: tr.newDoc.length,
                  insert: tr.newDoc.toString().replace(/\s*\n\s*/g, SEPARATOR)
                },
                sequential: true
              }
            ]
          : tr
      ),
      keymap.of([
        {
          key: 'Enter',
          run: (editor) => {
            if (completionStatus(editor.state) === 'active') return false;
            const doc = editor.state.doc.toString();
            if (!doc.trim() || doc.trimEnd().endsWith(',')) return true;
            editor.dispatch({
              changes: { from: doc.length, insert: SEPARATOR },
              selection: { anchor: doc.length + SEPARATOR.length }
            });
            return true;
          }
        },
        { key: 'ArrowDown', run: startCompletion },
        ...defaultKeymap,
        ...historyKeymap
      ]),
      // A typed comma always brings its space, so chips stay evenly spaced.
      EditorView.inputHandler.of((editor, from, to, text) => {
        if (text !== ',') return false;
        editor.dispatch({
          changes: { from, to, insert: SEPARATOR },
          selection: { anchor: from + SEPARATOR.length }
        });
        return true;
      }),
      options.length
        ? autocompletion({ override: [suggestionSource(options)], icons: false })
        : [],
      chips,
      // Earlier extensions win in CodeMirror: the token theme must precede the shared one.
      tokenTheme,
      ziggizEditorTheme,
      placeholder ? placeholderText(placeholder) : [],
      EditorState.readOnly.of(disabled),
      EditorView.editable.of(!disabled),
      EditorView.contentAttributes.of({ 'aria-label': label, 'aria-multiline': 'false' }),
      EditorView.updateListener.of((update) => {
        if (!update.docChanged) return;
        const next = parseTokens(update.state.doc.toString());
        if (next.join('\n') !== handlers.current.values.join('\n'))
          handlers.current.onChange(next);
      })
    ];
    const editor = new EditorView({
      parent: host.current,
      state: EditorState.create({
        doc: handlers.current.values.join(SEPARATOR),
        extensions
      })
    });
    view.current = editor;
    return () => {
      editor.destroy();
      view.current = null;
    };
  }, [label, placeholder, disabled, suggestionKey]);

  // Values changed from outside (Reset, another control) replace the text.
  useEffect(() => {
    const editor = view.current;
    if (!editor) return;
    if (parseTokens(editor.state.doc.toString()).join('\n') === values.join('\n')) return;
    editor.dispatch({
      changes: { from: 0, to: editor.state.doc.length, insert: values.join(SEPARATOR) }
    });
  }, [values]);

  return (
    <div
      ref={host}
      className={cn(
        'min-h-10 w-full min-w-0 rounded-md border border-input focus-within:border-brand focus-within:ring-2 focus-within:ring-ring/30',
        disabled && 'opacity-50',
        className
      )}
    />
  );
};
